import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Languages, User } from 'lucide-react';

/** Partner marks shown in mobile drawer — isolated styling so OS/browser dark mode cannot wash them out */
const DRAWER_TRUST_LOGOS = [
  { name: 'Clidrive', src: '/logos/clidrive.svg' },
  { name: 'Omocom', src: '/logos/omocom.png' },
  { name: 'Amovens', src: '/logos/amovens.svg' },
  { name: 'Ayvens', src: '/logos/ayvens.svg' },
  { name: 'Prodalim', src: '/logos/prodalim.svg' },
];

/**
 * Full-screen overlay + slide-in panel for marketing pages (xl breakpoint hides this — desktop nav is used).
 */
export function MarketingMobileNavDrawer({
  open,
  onClose,
  links,
  language,
  onToggleLanguage,
  loginLabel,
  loginTo = '/login',
  onLoginClick,
  showPartnerLogos = true,
  partnersTitle = 'Confían en AutoGo',
}) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const linkClass =
    'block rounded-xl px-4 py-3.5 text-base font-medium text-white/90 hover:text-white hover:bg-white/[0.06] border border-transparent hover:border-white/10 transition-colors';

  return (
    <>
      <button
        type="button"
        className="fixed inset-0 z-[60] bg-black/75 backdrop-blur-sm xl:hidden"
        aria-label="Close navigation"
        onClick={onClose}
      />
      <aside
        className="fixed inset-y-0 right-0 z-[70] flex w-[min(100%,22rem)] flex-col border-l border-white/10 bg-[#060B14]/98 shadow-[-12px_0_40px_rgba(0,0,0,0.45)] backdrop-blur-xl xl:hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="marketing-mobile-nav-title"
      >
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-3.5">
          <span id="marketing-mobile-nav-title" className="font-mono text-[10px] tracking-[0.35em] text-[#00F260]">
            MENÚ
          </span>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-white/80 hover:bg-white/10 hover:text-white"
            aria-label="Cerrar menú"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4" data-testid="marketing-mobile-nav">
          {links.map((item) => {
            const common = { className: linkClass, onClick: onClose };
            if (item.to) {
              return (
                <Link key={item.key || item.to} to={item.to} {...common} data-testid={item.testId}>
                  {item.label}
                </Link>
              );
            }
            return (
              <a key={item.key || item.href} href={item.href} {...common} data-testid={item.testId}>
                {item.label}
              </a>
            );
          })}
        </nav>

        <div className="border-t border-white/10 px-4 py-4 space-y-3">
          <button
            type="button"
            onClick={() => {
              onToggleLanguage();
            }}
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/15 py-3 text-xs font-mono uppercase tracking-widest text-[#CBD5E1] hover:border-[#00F260]/40 hover:text-white"
          >
            <Languages className="h-4 w-4" />
            {language === 'es' ? 'English' : 'Español'}
          </button>
          {loginLabel &&
            (onLoginClick ? (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onLoginClick();
                }}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/15 py-3 text-sm font-medium text-white hover:border-[#00F260] hover:text-[#00F260]"
              >
                <User className="h-4 w-4" />
                {loginLabel}
              </button>
            ) : (
              <Link
                to={loginTo}
                onClick={onClose}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/15 py-3 text-sm font-medium text-white hover:border-[#00F260] hover:text-[#00F260]"
              >
                <User className="h-4 w-4" />
                {loginLabel}
              </Link>
            ))}
        </div>

        {showPartnerLogos && (
          <div className="border-t border-white/10 px-4 py-5 pb-[max(1.25rem,env(safe-area-inset-bottom,0px))]">
            <p className="mb-3 text-center font-mono text-[9px] tracking-[0.28em] text-[#00F260]">{partnersTitle}</p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {DRAWER_TRUST_LOGOS.map((c) => (
                <div
                  key={c.name}
                  className="flex items-center justify-center rounded-lg bg-white/[0.12] px-3 py-2 ring-1 ring-white/15 [forced-color-adjust:none]"
                >
                  <img
                    src={c.src}
                    alt={c.name}
                    className="h-6 w-auto max-w-[100px] object-contain [forced-color-adjust:none]"
                    style={{ filter: 'brightness(0) invert(1)', opacity: 0.95 }}
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </aside>
    </>
  );
}
